import {Component, Injector, OnDestroy, ViewChild} from '@angular/core';
import {FieldProps, SearchPage, SessionObject} from '@twpub/core/models';
import {Subscription} from 'rxjs';
import {distinctUntilChanged, map, skip} from 'rxjs/operators';
import {BasePageComponent} from '@twpub/shared/components/base';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {MatSidenav} from '@angular/material/sidenav';
import {NgTemplateOutlet} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {FeatureFlagService, SharedStateService} from '@twpub/core/services';
import {DictionaryService} from '@twpub/core/services/dictionary.service';
import {TenantService} from '@twpub/core/services/tenant.service';
import {SecurityConfigService} from '@twpub/shared/services/security-config.service';
import {SuggestTermDialogComponent} from '@twpub/shared/components/suggest-term-dialog/suggest-term-dialog.component';
import {
  DictionaryWrapperComponent,
  DomainWrapperComponent,
  FilterWrapperComponent,
  FooterWrapperComponent,
  HeaderWrapperComponent,
  PreambleWrapperComponent,
  ResultListWrapperComponent,
  SearchFieldWrapperComponent,
  SectionWrapperComponent,
  SourceLanguageWrapperComponent,
  TargetLanguageWrapperComponent
} from '@twpub/shared/components/wrappers';

@Component({
  selector: 'pub-search-page',
  templateUrl: './default-search-page.component.html',
  styleUrls: ['./default-search-page.component.scss'],
  standalone: true,
  imports: [HeaderWrapperComponent, PreambleWrapperComponent, NgTemplateOutlet, FooterWrapperComponent, FilterWrapperComponent, SourceLanguageWrapperComponent, TargetLanguageWrapperComponent, DictionaryWrapperComponent, SectionWrapperComponent, DomainWrapperComponent, SearchFieldWrapperComponent, ResultListWrapperComponent, MatIcon]
})
export class DefaultSearchPageComponent extends BasePageComponent implements OnDestroy {
  @ViewChild('sidenav') sidenav!: MatSidenav;

  showSidebar: boolean = false;
  showSuggestButton: boolean = false;

  private dialog: MatDialog;
  private dictionaryService: DictionaryService;
  private tenantService: TenantService;
  private securityConfigService: SecurityConfigService;
  private sharedState: SharedStateService;
  private featureFlagService: FeatureFlagService;
  private qualifyingDictionaries: { id: number; name: string }[] = [];
  private suggestionSub?: Subscription;

  constructor(injector: Injector) {
    super(injector);
    this.dialog = injector.get(MatDialog);
    this.dictionaryService = injector.get(DictionaryService);
    this.tenantService = injector.get(TenantService);
    this.securityConfigService = injector.get(SecurityConfigService);
    this.sharedState = injector.get(SharedStateService);
    this.featureFlagService = injector.get(FeatureFlagService);
  }

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [
      {
        key: 'showSidebar',
        label: 'Show sidebar',
        controlType: ControlType.Boolean,
        description: 'Display filtering components in sidebar'
      }
    ];

    constructor() {
      super(DefaultSearchPageComponent, 'DefaultSearchPageComponent', 'Default Search page', [ComponentCategory.SearchPage]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<SearchPage>(DefaultSearchPageComponent, this.name, initialConfig);
    }
  }

  override ngOnInit() {
    super.ngOnInit();
    this.showSidebar = this.config?.getBoolean('showSidebar', false) || false;
    // Feature flags load asynchronously; wait for them before reading TermSuggestions,
    // otherwise on a fresh page load the flag reads as its default (false) and the button never appears.
    this.featureFlagService.waitForReady(() => this.initSuggestButton());
    // Re-evaluate only when the dictionary selection actually changes.
    // Every updateSession spreads the previous session, so dictIds is present on every emission —
    // a hasOwnProperty filter would re-fire (and flicker the icon) on every concept open.
    this.suggestionSub = this.sharedState.session$
      .pipe(
        map((s: SessionObject) => s?.dictIds ?? []),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        skip(1)
      )
      .subscribe(() => this.featureFlagService.isReady() && this.initSuggestButton());
  }

  ngOnDestroy(): void {
    this.suggestionSub?.unsubscribe();
  }

  private initSuggestButton(): void {
    this.showSuggestButton = false;
    if (!this.featureFlagService.isFeatureEnabled('TermSuggestions', false)) {
      return;
    }
    // When anonymous access is enabled the search page skips Keycloak init at startup,
    // so a logged-in user's session isn't visible here yet — make sure it has been checked.
    this.securityConfigService.ensureKeycloakSessionChecked()
      .then(() => {
        if (!this.securityConfigService.isUserAuthenticated()) {
          return;
        }
        this.tenantService.getCurrentTenant().subscribe({
          next: (tenant) => {
            if (!tenant.suggestionsEnabled) {
              return;
            }
            const enabledDictIds = Object.keys(tenant.dictionarySuggestionSections || {}).map(Number);
            if (enabledDictIds.length === 0) {
              return;
            }
            this.dictionaryService.getDictionaries(true).subscribe({
              next: (dicts) => {
                const selected = this.sharedState.getDictionaryIds();
                const effectiveSelectedIds = (selected && selected.length > 0)
                  ? selected
                  : dicts.map(d => d.id);
                this.qualifyingDictionaries = dicts
                  .filter(d => enabledDictIds.includes(d.id) && effectiveSelectedIds.includes(d.id))
                  .map(d => ({id: d.id, name: d.name}));
                this.showSuggestButton = this.qualifyingDictionaries.length > 0;
              }
            });
          }
        });
      })
      .catch(err => this.logger.debug('SuggestButton: Keycloak session check failed', err));
  }

  openSuggestDialog(): void {
    const sourceLangs = this.sharedState.getSourceLangs();
    const defaultLanguageCode = sourceLangs.length > 0 ? sourceLangs[0] : undefined;
    const selected = this.sharedState.getDictionaryIds();
    const defaultDictionaryId = (selected && selected.length === 1
      && this.qualifyingDictionaries.some(d => d.id === selected[0])) ? selected[0] : undefined;
    this.dialog.open(SuggestTermDialogComponent, {
      data: {dictionaries: this.qualifyingDictionaries, defaultDictionaryId, defaultLanguageCode},
      width: '500px'
    });
  }
}
